import type { ColDef, EditableCallback } from "ag-grid-community"
import { min, sum, sumBy } from "lodash"

import type { IVariantSummary } from "~/hooks/useVariantTableData"
import type { RouterInputs } from "~/utils/api"
import {
  createAverageColumn,
  createDateColumn,
  createSumColumn,
  createUniqueNumberColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { formatSecondaryMOQName, getServiceLevelLabel } from "~/utils/formatting"
import { isLifecyclePhasedOut } from "~/utils/lifecycleUtils"
import { extractVariantSummaries, ProductTableColumn } from "~/utils/productTableUtils"

import type { ColumnSpec } from "./types"

/** A SKU metadata field the table can edit in place. */
export type MetadataField = keyof RouterInputs["product"]["saveMetadata"]

/**
 * Builds the editing half of a metadata column. A field the ERP owns comes back read-only, with
 * a tooltip in place of the template's own explaining why.
 */
export type MetadataEditor = (
  fieldName: MetadataField,
  editable: ColDef<IVariantSummary>["editable"],
) => Pick<ColDef<IVariantSummary>, "valueSetter" | "editable" | "tooltip" | "tooltipComponent">

export interface SupplyColumnCtx {
  isEditable: EditableCallback<IVariantSummary>
  metadataEditor: MetadataEditor
  currency: string | undefined
  includeFilter: boolean
  /** Set when the table is scoped to one warehouse; otherwise figures span all of them. */
  warehouseId: string | undefined
  aggregations: string[]
}

type SupplySpec = ColumnSpec<IVariantSummary, SupplyColumnCtx>

/** The selected warehouse's safety-stock buffer, or the total across all of them. */
export function bufferQuantity(row: IVariantSummary, warehouseId: string | undefined): number {
  if (warehouseId) {
    return sum(row.stockOutInfoWarehouse?.[warehouseId]?.map((o) => o.buffer ?? 0) ?? [])
  }
  return (
    sum(
      Object.values(row.stockOutInfoWarehouse ?? {}).flatMap((warehouseData) =>
        warehouseData.map((o) => o.buffer ?? 0),
      ),
    ) ?? 0
  )
}

/** Capital tied up in safety stock: buffer units across all warehouses at cost price. */
export function safetyStockValue(row: IVariantSummary): number {
  return (
    (sumBy(
      Object.values(row.stockOutInfoWarehouse ?? {}).flatMap((warehouseData) =>
        warehouseData.map((o) => o.buffer ?? 0),
      ),
      (val) => val,
    ) ?? 0) * (row.costPrice ?? 0)
  )
}

/** Earliest projected break into safety stock. Phased-out variants get no projection. */
export function safetyStockOutDate(row: IVariantSummary): Date | undefined | null {
  if (isLifecyclePhasedOut([row])) return null
  return min(row.outOfStockInformation?.flatMap((o) => o?.safetyStockOutDate))
}

/**
 * The preferred vendor's name, falling back to its raw id, and to a placeholder when several
 * vendors supply the variant but none is preferred.
 */
export function preferredVendorName(row: IVariantSummary): string | undefined {
  const preferredVendor = row.settings.preferredVendorId
  const name = row.vendors.find((v) => v.id === preferredVendor)?.name
  if (preferredVendor) return name || preferredVendor
  if (row.vendors.length) return "Multiple vendors"
  return
}

/** Price columns share their aggregation, filter and currency subtitle. */
function priceColumn(
  colId: ProductTableColumn,
  field: "costPrice" | "recommendedRetailPrice" | "wholesalePrice",
  ctx: SupplyColumnCtx,
): ColDef<IVariantSummary> {
  return createAverageColumn<IVariantSummary>({
    colId,
    field,
    headerSubtitle: ctx.currency,
    filter: ctx.includeFilter ? "agNumberColumnFilter" : undefined,
    // Named aggFunc (registered in DataTableV2) — a function reference here
    // would be dropped on column-state restoration, leaving the column
    // value-inactive and aggData empty.
    aggFunc: "weightedAverage",
    context: { weightKey: "stockLevel" },
    decimals: 2,
  })
}

export const SUPPLY_COLUMN_SPECS: readonly SupplySpec[] = [
  {
    colId: ProductTableColumn.MinimumOrderQuantity,
    calculate: (row) => row.settings.minimumOrderQuantity ?? "",
    colDef: (ctx) =>
      createUniqueNumberColumn<IVariantSummary>({
        colId: ProductTableColumn.MinimumOrderQuantity,
        tooltipTitle: "SKU MOQs",
        ...ctx.metadataEditor("minimumOrderQuantity", ctx.isEditable),
      }),
  },
  {
    colId: ProductTableColumn.SecondaryMOQ,
    calculate: (row) => row.settings.secondaryMoq,
    colDef: (ctx) =>
      createUniqueNumberColumn<IVariantSummary>({
        colId: ProductTableColumn.SecondaryMOQ,
        tooltipTitle: `${formatSecondaryMOQName()}s`,
        ...ctx.metadataEditor("secondaryMoq", ctx.isEditable),
      }),
  },
  {
    colId: ProductTableColumn.CostPrice,
    colDef: (ctx) => priceColumn(ProductTableColumn.CostPrice, "costPrice", ctx),
  },
  {
    colId: ProductTableColumn.RecommendedRetailPrice,
    colDef: (ctx) =>
      priceColumn(ProductTableColumn.RecommendedRetailPrice, "recommendedRetailPrice", ctx),
  },
  {
    colId: ProductTableColumn.WholesalePrice,
    colDef: (ctx) => priceColumn(ProductTableColumn.WholesalePrice, "wholesalePrice", ctx),
  },
  {
    colId: ProductTableColumn.Buffer,
    calculate: (row, ctx) => bufferQuantity(row, ctx.warehouseId),
    colDef: () => createSumColumn<IVariantSummary>({ colId: ProductTableColumn.Buffer }),
  },
  {
    colId: ProductTableColumn.SafetyStockValue,
    calculate: safetyStockValue,
    colDef: (ctx) =>
      createSumColumn<IVariantSummary>({
        colId: ProductTableColumn.SafetyStockValue,
        suffix: ctx.currency ?? "DKK",
        useFormatWithSuffix: true,
        decimals: 1,
      }),
  },
  {
    colId: ProductTableColumn.SafetyStockOutDate,
    calculate: safetyStockOutDate,
    colDef: () =>
      createDateColumn<IVariantSummary>({
        colId: ProductTableColumn.SafetyStockOutDate,
        initialWidth: 130,
        cellClass: (params) => {
          const entries: IVariantSummary[] = extractVariantSummaries(params)
          if (isLifecyclePhasedOut(entries)) return "text-fg-muted"
        },
        tooltip: (params) => {
          const entries: IVariantSummary[] = extractVariantSummaries(params)
          if (isLifecyclePhasedOut(entries))
            return { unavailableMessage: "Phased out. No safety stock projection" }
          return undefined
        },
      }),
  },
  {
    colId: ProductTableColumn.LeadTime,
    calculate: (row) => row.settings.totalLeadTimeDays,
    colDef: (ctx) =>
      createUniqueNumberColumn<IVariantSummary>({
        colId: ProductTableColumn.LeadTime,
        tooltipTitle: "Total lead time",
        suffix: " days",
        valueParser: (params) => Number(params.newValue),
        ...ctx.metadataEditor("totalLeadTimeDays", ctx.isEditable),
      }),
  },
  {
    colId: ProductTableColumn.ServiceLevel,
    calculate: (row) =>
      row.settings.bufferType === "serviceLevel" ? row.settings.bufferValue : undefined,
    colDef: () =>
      createAverageColumn<IVariantSummary>({
        colId: ProductTableColumn.ServiceLevel,
        valueFormatter: (params) => {
          // Under aggregation a group cell holds { count, value } rather than a bare number.
          if (params.node?.group) {
            return params.value?.value ? getServiceLevelLabel(params.value.value) : "-"
          } else {
            return params.value ? getServiceLevelLabel(params.value) : "-"
          }
        },
      }),
  },
  {
    colId: ProductTableColumn.ColliSize,
    calculate: (row) => row.settings.colliSize,
    colDef: (ctx) =>
      createUniqueNumberColumn<IVariantSummary>({
        colId: ProductTableColumn.ColliSize,
        tooltipTitle: "Colli sizes",
        suffix: " units",
        ...ctx.metadataEditor("colliSize", ctx.isEditable),
      }),
  },
  {
    colId: ProductTableColumn.WeekCover,
    calculate: (row) =>
      row.settings.bufferType === "weekCover" ? (row.settings.bufferValue as number) : undefined,
    colDef: () =>
      createUniqueNumberColumn<IVariantSummary>({
        colId: ProductTableColumn.WeekCover,
        suffix: "weeks",
        tooltipTitle: "Week covers",
      }),
  },
  {
    colId: ProductTableColumn.PurchaseInterval,
    calculate: (row) => row.settings.purchaseIntervalDays,
    colDef: (ctx) =>
      createUniqueNumberColumn<IVariantSummary>({
        colId: ProductTableColumn.PurchaseInterval,
        tooltipTitle: "Purchase intervals",
        suffix: "days",
        ...ctx.metadataEditor("purchaseIntervalDays", ctx.isEditable),
      }),
  },
  {
    colId: ProductTableColumn.Vendor,
    calculate: (row, ctx) =>
      ctx.aggregations.includes("Vendor") ? row.groupVendor : row.vendors.map((v) => v.name),
    colDef: () =>
      createUniqueStringColumn<IVariantSummary>({
        colId: ProductTableColumn.Vendor,
        field: "groupVendor",
        rowGroup: true,
        tooltipTitle: "Vendors",
      }),
  },
  {
    colId: ProductTableColumn.PreferredVendor,
    calculate: preferredVendorName,
    colDef: () =>
      createUniqueStringColumn<IVariantSummary>({
        colId: ProductTableColumn.PreferredVendor,
        tooltipTitle: "Preferred Vendors",
      }),
  },
  {
    colId: ProductTableColumn.SupplierSku,
    colDef: () =>
      createUniqueStringColumn<IVariantSummary>({
        colId: ProductTableColumn.SupplierSku,
        field: "supplierSku",
        tooltipTitle: "Supplier SKUs",
        uniqueSuffix: "supplier SKUs",
        isIdentifier: true,
      }),
  },
  {
    colId: ProductTableColumn.SoldSince,
    colDef: () =>
      createDateColumn<IVariantSummary>({
        colId: ProductTableColumn.SoldSince,
        field: "soldSince",
      }),
  },
]
